import { MainContext } from "@context";
import ChevronIcon from "@layouts/betto/assets/chevronRight.svg";
import moneyIcon from "@layouts/betto/assets/money.svg";
import TrophyIcon from "@layouts/betto/assets/trophyIcon.svg";
import DefaultCasinoIcon from "@pages/casino/assets/backgrounds/default-casino-icon.png";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getTranslate } from "react-localize-redux";
import { useSelector } from "react-redux";
import {
	formatAmountParts,
	formatDate,
	getBettoImageLoadingProps,
	preloadBettoImages,
} from "../utils";
import {
	AmountDecimals,
	AmountPill,
	AmountText,
	ArrowButton,
	ArrowIcon,
	CardBody,
	CardInner,
	CardMeta,
	Controls,
	DateText,
	GameName,
	HEADER,
	MetaDot,
	ThumbWrap,
	TitleBox,
	Track,
	Viewport,
	WinCard,
	WinnerText,
	WRAPPER,
} from "./elements";
import useController from "./useController";

const getImageUrl = (url) => (typeof url === "string" && url.trim() ? url.trim() : null);

const getWinImage = (win) =>
	getImageUrl(win?.icon_custom_url) ||
	getImageUrl(win?.game_icon_url) ||
	getImageUrl(win?.icon_high) ||
	DefaultCasinoIcon;

const GAP = 8;
const CARD_WIDTH = 244;
const STEP = 3;
const AUTO_SCROLL_SPEED = 36;
const MANUAL_RESUME_DELAY = 2000;

const getSetWidth = (count) => count * CARD_WIDTH + Math.max(0, count - 1) * GAP;

const WinSlideCard = memo(({ item, itemIndex, onCardClick, onImageError }) => {
	const { whole, decimals } = formatAmountParts(item.amount);
	const imageLoadingProps = getBettoImageLoadingProps(itemIndex, { eagerCount: 6 });

	return (
		<WinCard
			type="button"
			onClick={() => onCardClick(item)}>
			<CardInner>
				<ThumbWrap>
					<img
						src={getWinImage(item)}
						alt=""
						width={90}
						height={90}
						{...imageLoadingProps}
						onError={(event) => onImageError(event, item)}
					/>
				</ThumbWrap>
				<CardBody>
					<GameName>{item.remote_name || item.gameRemoteName}</GameName>
					<CardMeta>
						{item.givenName && <WinnerText>{item.givenName}</WinnerText>}
						{item.givenName && <MetaDot aria-hidden="true">·</MetaDot>}
						<DateText>{formatDate(itemIndex % 14)}</DateText>
					</CardMeta>
					<AmountPill>
						<img
							src={moneyIcon}
							alt=""
						/>
						<AmountText>
							{whole}
							{decimals && <AmountDecimals>{decimals}</AmountDecimals>}
						</AmountText>
					</AmountPill>
				</CardBody>
			</CardInner>
		</WinCard>
	);
});

WinSlideCard.displayName = "WinSlideCard";

const selectTranslate = (state) => getTranslate(state.localize);

export const BettoWonSlider = ({ hideHeader = false }) => {
	const translate = useSelector(selectTranslate);
	const { isMobile } = useContext(MainContext).state;
	const {
		state: { lastWinners, isLoading },
		handlers: { handleCardClick },
	} = useController();

	const viewportRef = useRef(null);
	const trackRef = useRef(null);
	const autoOffsetRef = useRef(0);
	const manualOffsetRef = useRef(0);
	const singleSetWidthRef = useRef(0);
	const maxOffsetRef = useRef(0);
	const isPausedRef = useRef(true);
	const rafRef = useRef(null);
	const lastFrameRef = useRef(null);
	const manualIdleTimerRef = useRef(null);

	const [hasNav, setHasNav] = useState(false);
	const [manualOffset, setManualOffset] = useState(0);
	const [isManual, setIsManual] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const items = lastWinners ?? [];

	useEffect(() => {
		if (!items.length) return;
		preloadBettoImages(items.slice(0, 8).map(getWinImage));
	}, [items]);

	const loopItems = useMemo(() => (isMobile ? items : [...items, ...items]), [items, isMobile]);

	const cardStep = CARD_WIDTH + GAP;

	const applyAutoTransform = useCallback((offset) => {
		const track = trackRef.current;
		if (!track) return;
		track.style.transform = `translate3d(-${offset}px,0,0)`;
	}, []);

	const clearTrackInlineStyles = useCallback(() => {
		const track = trackRef.current;
		if (!track) return;
		track.style.transform = "";
		track.style.transition = "";
		track.style.willChange = "";
	}, []);

	const syncMeasurements = useCallback(() => {
		const viewport = viewportRef.current;
		if (!viewport) return;

		const viewportWidth = viewport.offsetWidth;
		const singleSetWidth = getSetWidth(items.length);
		const maxOffset = viewportWidth > 0 ? Math.max(0, singleSetWidth - viewportWidth) : 0;

		singleSetWidthRef.current = singleSetWidth;
		maxOffsetRef.current = maxOffset;

		setHasNav((current) => {
			const nextHasNav = maxOffset > 0;
			return current === nextHasNav ? current : nextHasNav;
		});

		setManualOffset((current) => Math.min(current, maxOffset));
		manualOffsetRef.current = Math.min(manualOffsetRef.current, maxOffset);
	}, [items.length]);

	const updatePauseState = useCallback(() => {
		const canAutoScroll = !isMobile && maxOffsetRef.current > 0 && items.length > 1;
		isPausedRef.current = isHovered || isManual || !canAutoScroll;
	}, [isHovered, isManual, isMobile, items.length]);

	useEffect(() => {
		syncMeasurements();

		const viewport = viewportRef.current;
		if (!viewport) return undefined;

		const resizeObserver = new ResizeObserver(syncMeasurements);
		resizeObserver.observe(viewport);

		return () => resizeObserver.disconnect();
	}, [syncMeasurements]);

	useEffect(() => {
		updatePauseState();
	}, [updatePauseState]);

	useEffect(() => {
		manualOffsetRef.current = manualOffset;
	}, [manualOffset]);

	useEffect(() => {
		return () => {
			if (manualIdleTimerRef.current) {
				clearTimeout(manualIdleTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (isMobile) {
			clearTrackInlineStyles();
			setIsManual(false);
			setManualOffset(0);
			manualOffsetRef.current = 0;
			autoOffsetRef.current = 0;
			if (viewportRef.current) {
				viewportRef.current.scrollLeft = 0;
			}
		}
	}, [isMobile, clearTrackInlineStyles]);

	useEffect(() => {
		if (isMobile) return undefined;

		const animate = (time) => {
			if (lastFrameRef.current !== null && !isPausedRef.current) {
				const delta = time - lastFrameRef.current;
				const setWidth = singleSetWidthRef.current;

				autoOffsetRef.current += (AUTO_SCROLL_SPEED * delta) / 1000;
				if (setWidth > 0 && autoOffsetRef.current >= setWidth) {
					autoOffsetRef.current -= setWidth;
				}

				applyAutoTransform(autoOffsetRef.current);
			}

			lastFrameRef.current = time;
			rafRef.current = requestAnimationFrame(animate);
		};

		rafRef.current = requestAnimationFrame(animate);

		return () => {
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			lastFrameRef.current = null;
		};
	}, [applyAutoTransform, isMobile]);

	const resumeAutoScroll = useCallback(() => {
		autoOffsetRef.current = manualOffsetRef.current % (singleSetWidthRef.current || 1);
		clearTrackInlineStyles();
		setIsManual(false);
	}, [clearTrackInlineStyles]);

	const scheduleAutoResume = useCallback(() => {
		if (manualIdleTimerRef.current) {
			clearTimeout(manualIdleTimerRef.current);
		}

		manualIdleTimerRef.current = setTimeout(() => {
			resumeAutoScroll();
			manualIdleTimerRef.current = null;
		}, MANUAL_RESUME_DELAY);
	}, [resumeAutoScroll]);

	const enterManualAt = useCallback(
		(offset) => {
			clearTrackInlineStyles();

			const clampedOffset = Math.max(0, Math.min(offset, maxOffsetRef.current));

			setIsManual(true);
			setManualOffset(clampedOffset);
			manualOffsetRef.current = clampedOffset;
			scheduleAutoResume();
		},
		[clearTrackInlineStyles, scheduleAutoResume]
	);

	const getCurrentOffset = useCallback(() => {
		if (isManual) return manualOffsetRef.current;
		const setWidth = singleSetWidthRef.current;
		return setWidth > 0 ? autoOffsetRef.current % setWidth : 0;
	}, [isManual]);

	const prev = () => {
		const current = getCurrentOffset();
		const prevOffset = current - STEP * cardStep;
		enterManualAt(prevOffset < 0 ? maxOffsetRef.current : prevOffset);
	};

	const next = () => {
		const current = getCurrentOffset();
		const nextOffset = current + STEP * cardStep;
		enterManualAt(nextOffset > maxOffsetRef.current ? 0 : nextOffset);
	};

	const handleImageError = useCallback((event, item) => {
		const image = event.currentTarget;
		const customUrl = getImageUrl(item?.icon_custom_url);
		const gameIconUrl = getImageUrl(item?.game_icon_url);

		if (customUrl && gameIconUrl && image.dataset.fallback !== "game") {
			image.dataset.fallback = "game";
			image.src = gameIconUrl;
			return;
		}

		image.onerror = null;
		image.src = DefaultCasinoIcon;
	}, []);

	if (isLoading || !items.length) return null;

	return (
		<WRAPPER $hideHeader={hideHeader}>
			{!hideHeader && (
				<HEADER>
					<TitleBox>
						<img
							src={TrophyIcon}
							alt="Trophy"
						/>
						{translate("Recent_Wins")}
					</TitleBox>
					{hasNav && (
						<Controls>
							<ArrowButton
								type="button"
								$variant="prev"
								onClick={prev}
								aria-label="Previous">
								<ArrowIcon
									src={ChevronIcon}
									alt=""
									$mirrored
								/>
							</ArrowButton>
							<ArrowButton
								type="button"
								$variant="next"
								onClick={next}
								aria-label="Next">
								<ArrowIcon
									src={ChevronIcon}
									alt=""
								/>
							</ArrowButton>
						</Controls>
					)}
				</HEADER>
			)}

			<Viewport
				innerRef={viewportRef}
				$touchScroll={isMobile}
				onMouseEnter={isMobile ? undefined : () => setIsHovered(true)}
				onMouseLeave={isMobile ? undefined : () => setIsHovered(false)}>
				<Track
					innerRef={trackRef}
					$gap={GAP}
					$offset={!isMobile && isManual ? manualOffset : undefined}
					$smooth={!isMobile && isManual}>
					{loopItems.map((item, i) => {
						const itemIndex = i % items.length;

						return (
							<WinSlideCard
								key={`betto-won-${item.gameId || itemIndex}-${i}`}
								item={item}
								itemIndex={itemIndex}
								onCardClick={handleCardClick}
								onImageError={handleImageError}
							/>
						);
					})}
				</Track>
			</Viewport>
		</WRAPPER>
	);
};
